import React, { useEffect, useRef, useState } from "react";
import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng
} from "react-google-places-autocomplete";
import styled from "styled-components";
import { displayLocation, Location } from "sweet-global";
import LocationIcon from "sweet-web/components/icons/LocationIcon";
import { ItemLabel, ItemTitle } from "sweet-web/components/universal/Text";
import { colors } from "sweet-web/lib/styles";
import { globalInputStyles, IconWrapper } from "./index";

/*
Location Input
When unfocused, it searches a google api for the location query and matches to a specific place with longitude, and latitude
*/

const Wrapper = styled.div`
  padding: 10px 0 10px 0;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  ${globalInputStyles}
  width: 100%;
  padding-left: 35px;
`;

const Suggestions = styled.div`
  border-radius: 5px;
  position: absolute;
  box-shadow: rgba(0, 0, 0, 0.08) 0 3px 10px 0;
  z-index: 100;
  width: 100%;
  overflow: hidden;
  border: 1px solid ${colors.lightGray};
`;

const Suggestion = styled.div<{ active: boolean }>`
  color: ${props => (props.active ? colors.white : colors.nearBlack)};
  font-size: 14px;
  font-weight: 500;
  padding: 10px;
  width: 100%;
  background-color: ${props => (props.active ? colors.primary : colors.white)};
  &:hover,
  &:focus {
    background-color: ${colors.lighterGray};
  }
`;

type LocationType = "address" | "city";

const parseLocation = async (
  locationObj: {
    description: string;
  },
  locationType: LocationType
): Promise<Location> => {
  const location: Partial<Location> = {};
  // TODO parse location based on comma values and suggestion type
  const splitLocation = locationObj.description.split(", ");
  if (splitLocation.length === 4 && locationType === "address") {
    // 1 Main Street, Concord, MA, USA
    location.address = splitLocation[0];
    location.city = splitLocation[1];
    location.state = splitLocation[2];
    location.country = splitLocation[3];
  } else if (splitLocation.length === 3 && locationType === "address") {
    // 1 Main Street, Berlin, Germany
    location.address = splitLocation[0];
    location.city = splitLocation[1];
    location.country = splitLocation[2];
  } else if (splitLocation.length === 3 && locationType === "city") {
    // Concord, MA, USA
    location.city = splitLocation[0];
    location.state = splitLocation[1];
    location.country = splitLocation[2];
  } else if (splitLocation.length === 2 && locationType === "city") {
    // Berlin, Germany
    location.city = splitLocation[0];
    location.country = splitLocation[1];
  }

  // get lat lng values
  const results = await geocodeByAddress(locationObj.description);
  const coordinates = await getLatLng(results[0]);
  location.latitude = coordinates.lat;
  location.longitude = coordinates.lng;

  return location as Location;
};

type Props = {
  title?: string;
  placeholder?: string;
  location: Location;
  onLocationChange: (location: Location) => void;
  type?: LocationType;
  description?: string;
};
const LocationInput: React.FC<Props> = props => {
  const [value, setValue] = useState("");

  let firstSuggestion;

  useEffect(() => {
    setValue(props.location ? displayLocation(props.location) : "");
  }, [props.location]);

  return (
    <GooglePlacesAutocomplete
      autocompletionRequest={{
        types: props.type == "city" ? ["(cities)"] : ["address"]
      }}
      initialValue={value}
      loader={<div />}
      renderInput={inputProps => (
        <Wrapper>
          <ItemTitle>{props.title}</ItemTitle>
          {props.description && <ItemLabel>{props.description}</ItemLabel>}
          <Input
            {...inputProps}
            placeholder={props.placeholder}
            onBlur={async e => {
              if (
                e.target.value &&
                firstSuggestion &&
                (!props.location ||
                  displayLocation(props.location) !== e.target.value)
              ) {
                const parsedLocation = await parseLocation(
                  firstSuggestion,
                  props.type
                );
                setValue(displayLocation(parsedLocation));
                props.onLocationChange(parsedLocation);
              } else {
                setValue("");
              }
            }}
          />
          <IconWrapper>
            <LocationIcon size={14} />
          </IconWrapper>
        </Wrapper>
      )}
      renderSuggestions={(active, suggestions, onSelectSuggestion) => (
        <Suggestions>
          {suggestions.map((suggestion, i) => {
            if (i === 0) firstSuggestion = suggestion;

            return (
              <Suggestion
                key={`location-input-suggestion-${i}`}
                active={active && suggestion.id === active.id}
                onClick={event => onSelectSuggestion(suggestion, event)}
              >
                {suggestion.description}
              </Suggestion>
            );
          })}
        </Suggestions>
      )}
      onSelect={async location => {
        const parsedLocation = await parseLocation(location, props.type);
        setValue(displayLocation(parsedLocation));
        props.onLocationChange(parsedLocation);
      }}
    />
  );
};
LocationInput.defaultProps = {
  title: "Location",
  type: "city"
};

export default LocationInput;
